import { useEffect, useRef, useState, type ReactNode } from 'react'
import type { EventPageViewer } from '../data/types'
import { truthy } from '../data/types'
import styles from './Header.module.css'

// Mirrors webshared/includes/_navbar.cfm exactly - the user asked for this page's top nav to match
// the legacy site's real header, not a redesigned one, so pages keep one consistent nav no matter
// which one a racer happens to be on. Same links/labels/dropdown grouping, real logo asset
// (/shared/images/logo.png - a real path in every environment, not per-event like a flyer).
//
// Legacy's mobile dropdowns genuinely inline-expand in place rather than floating (confirmed in
// webshared/css/all.min.css: `.navbar-nav .dropdown-menu{position:static}` is the base Bootstrap
// rule, and `.navbar-expand-md .navbar-nav .dropdown-menu{position:absolute}` only overrides that
// at >=768px) - so the mobile panel below uses its own accordion-style group component
// (MobileNavGroup/MobileProfileGroup), not the desktop NavDropdown's absolute-positioned menu.

type DropdownItem = { label: string; href: string; badge?: string }
type DropdownEntry = DropdownItem | { divider: true }

const TRACKS_LINKS: DropdownItem[] = [
  { label: 'US', href: '/tracks/?country=USA' },
  { label: 'Canada', href: '/tracks/?country=CANADA' },
  { label: 'Other', href: '/tracks/?country=OTHER' },
]

const EVENTS_LINKS: DropdownItem[] = [
  { label: 'RaceTime Entertainment', href: '/events/racetimeentertainment.cfm' },
  { label: 'All Events', href: '/events/index.cfm' },
  { label: 'By State', href: '/events/eventsByState.cfm' },
  { label: 'By Series/Promoter', href: '/events/eventsBySeries.cfm' },
  { label: 'Calendar', href: '/events/calendar.cfm' },
]

const REGISTER_LINKS: DropdownItem[] = [
  { label: 'Racer Registration', href: '/racers/register.cfm' },
  { label: 'Track Registration', href: '/tracks/register.cfm' },
  { label: 'Series/Promoter Registration', href: '/tracks/register.cfm' },
]

const LOGIN_LINKS: DropdownItem[] = [
  { label: 'Racer Login', href: '/racers/login.cfm' },
  { label: 'Track Login', href: '/tracks/login.cfm' },
  { label: 'Series/Promoter Login', href: '/tracks/login.cfm' },
]

// Mirrors _navbar.cfm's two logged-in dropdowns (racer branch / track branch) exactly, including
// the racer "Team" badge, which is legacy's own literal hardcoded "2" (not a real unread count) -
// kept verbatim for parity rather than fixed here.
function profileItems(viewer: EventPageViewer): DropdownEntry[] {
  if (viewer.type === 'track') {
    const items: DropdownEntry[] = [
      { label: 'My events', href: '/tracks/profile/myEvents.cfm' },
      { label: 'My broadcasts', href: '/tracks/profile/myBroadcasts.cfm' },
      { divider: true },
      { label: 'Account Profile', href: '/tracks/profile/editProfile.cfm' },
    ]
    if (!viewer.stripeMerchantId) {
      items.push({ label: 'Upgrade Account', href: '/tracks/profile/trackMembershipPro.cfm' })
    }
    items.push({ label: 'Logout', href: '/index.cfm?logout' })
    return items
  }

  const items: DropdownEntry[] = [
    { label: 'Dashboard', href: '/racers/profile/dashboard.cfm' },
    { label: 'Results', href: '/racers/profile/myResults.cfm' },
  ]
  if (truthy(viewer.teamManager)) {
    items.push({ label: 'Team', href: '/racers/profile/myTeam.cfm', badge: '2' })
  }
  items.push(
    { divider: true },
    { label: 'Account Information', href: '/racers/profile/editProfile.cfm' },
    { label: 'Logout', href: '/index.cfm?logout' },
  )
  return items
}

function DropdownItems({
  entries,
  className,
  itemClassName,
  dividerClassName,
}: {
  entries: DropdownEntry[]
  className: string
  itemClassName: string
  dividerClassName: string
}) {
  return (
    <div className={className}>
      {entries.map((entry, i) =>
        'divider' in entry ? (
          <div key={`divider-${i}`} className={dividerClassName} />
        ) : (
          <a key={entry.href + entry.label} href={entry.href} className={itemClassName}>
            {entry.label}
            {entry.badge ? <span className={styles.badge}>{entry.badge}</span> : null}
          </a>
        ),
      )}
    </div>
  )
}

// Desktop nav item: dropdown-menu floats absolutely below its trigger, matching legacy's
// >=768px behavior. `trigger` overrides the default "label + caret" button content (used by the
// logged-in profile dropdown's avatar+name trigger, which legacy also renders without a caret).
function NavDropdown({
  label,
  entries,
  align = 'left',
  trigger,
}: {
  label?: string
  entries: DropdownEntry[]
  align?: 'left' | 'right'
  trigger?: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [open])

  return (
    <div className={styles.dropdown} ref={ref}>
      <button type="button" className={styles.navLink} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {trigger ?? (
          <>
            {label}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </>
        )}
      </button>
      {open ? (
        <DropdownItems
          entries={entries}
          className={`${styles.dropdownMenu} ${align === 'right' ? styles.dropdownMenuRight : ''}`}
          itemClassName={styles.dropdownItem}
          dividerClassName={styles.dropdownDivider}
        />
      ) : null}
    </div>
  )
}

// Mobile nav item: inline-expands below its trigger (accordion), matching legacy's <768px
// behavior - not an absolute overlay like NavDropdown above.
function MobileNavGroup({ label, entries }: { label: string; entries: DropdownEntry[] }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={styles.mobileGroup}>
      <button
        type="button"
        className={`${styles.mobilePanelLink} ${styles.mobileGroupTrigger}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        {label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          className={open ? styles.chevronOpen : undefined}
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open ? (
        <DropdownItems entries={entries} className={styles.mobileSubList} itemClassName={styles.mobileSubLink} dividerClassName={styles.mobilePanelDivider} />
      ) : null}
    </div>
  )
}

function MobileProfileGroup({ viewer }: { viewer: EventPageViewer }) {
  const [open, setOpen] = useState(false)
  return (
    <div className={styles.mobileGroup}>
      <button
        type="button"
        className={`${styles.mobilePanelLink} ${styles.mobileGroupTrigger}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <span className={styles.profileTrigger}>
          <img src={viewer.profilePicUrl} alt="" className={styles.avatar} />
          {viewer.firstName} {viewer.lastName}
        </span>
      </button>
      {open ? (
        <DropdownItems
          entries={profileItems(viewer)}
          className={styles.mobileSubList}
          itemClassName={styles.mobileSubLink}
          dividerClassName={styles.mobilePanelDivider}
        />
      ) : null}
    </div>
  )
}

export function Header({ viewer }: { viewer: EventPageViewer }) {
  const loggedIn = truthy(viewer.loggedIn)
  const [mobileOpen, setMobileOpen] = useState(false)

  return (
    <header className={styles.header}>
      <a href="/index.cfm" className={styles.logo} aria-label="RCSignup home">
        <img src="/shared/images/logo.png" alt="RCSignup" height={36} />
      </a>

      <button type="button" className={styles.hamburger} aria-label="Open menu" aria-expanded={mobileOpen} onClick={() => setMobileOpen((o) => !o)}>
        {/* Matches the legacy navbar-toggler's Phosphor "dots-three" glyph (three plain dots, no
            outline circle - the circle read as an unwanted border around the icon) */}
        <svg width="22" height="22" viewBox="0 0 256 256" fill="currentColor">
          <circle cx="60" cy="128" r="16" />
          <circle cx="128" cy="128" r="16" />
          <circle cx="196" cy="128" r="16" />
        </svg>
      </button>

      <nav aria-label="Main" className={styles.nav}>
        <NavDropdown label="Tracks" entries={TRACKS_LINKS} />
        <NavDropdown label="Events" entries={EVENTS_LINKS} />
        <a href="/events/roarEvents.cfm" className={styles.navLink}>
          ROAR Events
        </a>
        <a href="/clubRaces/" className={styles.navLink}>
          Club Races
        </a>
      </nav>

      <div className={styles.right}>
        {loggedIn ? (
          <NavDropdown
            align="right"
            entries={profileItems(viewer)}
            trigger={
              <>
                <img src={viewer.profilePicUrl} alt="" className={styles.avatar} />
                <span>
                  {viewer.firstName} {viewer.lastName}
                </span>
              </>
            }
          />
        ) : (
          <>
            <NavDropdown label="Register" entries={REGISTER_LINKS} align="right" />
            <NavDropdown label="Login" entries={LOGIN_LINKS} align="right" />
          </>
        )}
      </div>

      {mobileOpen ? (
        <div className={styles.mobilePanel}>
          <MobileNavGroup label="Tracks" entries={TRACKS_LINKS} />
          <MobileNavGroup label="Events" entries={EVENTS_LINKS} />
          <a href="/events/roarEvents.cfm" className={styles.mobilePanelLink}>
            ROAR Events
          </a>
          <a href="/clubRaces/" className={styles.mobilePanelLink}>
            Club Races
          </a>
          <div className={styles.mobilePanelDivider} />
          {loggedIn ? (
            <MobileProfileGroup viewer={viewer} />
          ) : (
            <>
              <MobileNavGroup label="Register" entries={REGISTER_LINKS} />
              <MobileNavGroup label="Login" entries={LOGIN_LINKS} />
            </>
          )}
        </div>
      ) : null}
    </header>
  )
}
